<template>
  <section :class="embedded ? 'auth-embed' : 'auth-page auth-page--enterprise'">
    <article :class="embedded ? 'auth-embed__card' : 'auth-card auth-card--enterprise'">
      <div class="auth-shell">
        <aside class="auth-showcase">
          <span class="auth-showcase__badge">{{ activeScene.badge }}</span>
          <p class="auth-showcase__topline">{{ activeScene.topline }}</p>
          <h2 class="auth-showcase__title">{{ activeScene.title }}</h2>
          <p class="auth-showcase__lead">{{ activeScene.lead }}</p>

          <div class="auth-scene-chipbar">
            <span class="auth-scene-chip auth-scene-chip--primary">{{ activeScene.lines[0] }}</span>
            <span v-for="line in activeScene.lines.slice(1)" :key="line" class="auth-scene-chip">{{ line }}</span>
          </div>

          <div class="auth-card__metrics">
            <div class="auth-card__metric auth-card__metric--accent">
              <span>{{ activeScene.metricLabel }}</span>
              <strong>{{ activeScene.metricValue }}</strong>
            </div>
            <div class="auth-card__metric">
              <span>{{ activeScene.sideMetricLabel }}</span>
              <strong>{{ activeScene.sideMetricValue }}</strong>
            </div>
          </div>

          <div class="auth-assist">
            <p class="auth-assist__lead">{{ activeAssist.lead }}</p>
            <ul class="auth-assist__list">
              <li v-for="item in activeAssist.items" :key="item">{{ item }}</li>
            </ul>
          </div>
        </aside>

        <div class="auth-panel">
          <div class="auth-panel__head">
            <div>
              <p class="auth-card__eyebrow">账号认证</p>
              <h3 class="auth-panel__title">{{ activeTab === 'login' ? '登录后继续使用合同审查服务' : '注册账号后即可开始使用' }}</h3>
            </div>
          </div>

          <div class="auth-switcher" role="tablist" aria-label="认证方式切换">
            <button
              type="button"
              :class="['auth-switcher__item', { 'auth-switcher__item--active': activeTab === 'login' }]"
              @click="activeTab = 'login'"
            >
              登录
            </button>
            <button
              type="button"
              :class="['auth-switcher__item', { 'auth-switcher__item--active': activeTab === 'register' }]"
              @click="activeTab = 'register'"
            >
              注册
            </button>
          </div>

          <el-form v-if="activeTab === 'login'" label-position="top" class="auth-form auth-form--panel" @submit.prevent>
            <el-form-item label="邮箱">
              <el-input v-model="loginForm.email" placeholder="name@company.com" size="large" />
            </el-form-item>
            <el-form-item label="密码">
              <el-input v-model="loginForm.password" placeholder="请输入登录密码" size="large" show-password @keyup.enter="submitLogin" />
            </el-form-item>
            <el-button type="primary" size="large" class="full-width auth-form__submit" :loading="submitting" @click="submitLogin">
              登录并继续
            </el-button>
          </el-form>

          <el-form v-else label-position="top" class="auth-form auth-form--panel" @submit.prevent>
            <el-form-item label="用户名">
              <el-input v-model="registerForm.username" placeholder="请输入用户名" size="large" />
            </el-form-item>
            <el-form-item label="邮箱">
              <el-input v-model="registerForm.email" placeholder="name@company.com" size="large" />
            </el-form-item>
            <el-form-item label="密码">
              <el-input v-model="registerForm.password" placeholder="请设置登录密码" size="large" show-password @keyup.enter="submitRegister" />
            </el-form-item>
            <el-button type="primary" size="large" class="full-width auth-form__submit" :loading="submitting" @click="submitRegister">
              注册并进入工作台
            </el-button>
          </el-form>

          <p class="auth-panel__footer">
            <span>{{ activeTab === 'login' ? '还没有账号？' : '已有账号？' }}</span>
            <button type="button" class="auth-panel__link" @click="activeTab = activeTab === 'login' ? 'register' : 'login'">
              {{ activeTab === 'login' ? '立即注册' : '切换登录' }}
            </button>
          </p>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '../auth'

const props = withDefaults(defineProps<{
  embedded?: boolean
  initialTab?: 'login' | 'register'
  redirect?: string
}>(), {
  embedded: false,
  initialTab: 'login',
  redirect: '',
})

const emit = defineEmits<{
  authenticated: [target: string]
}>()

const auth = useAuth()
const route = useRoute()
const router = useRouter()
const activeTab = ref(props.initialTab)
const submitting = ref(false)

const loginForm = reactive({
  email: '',
  password: '',
})

const registerForm = reactive({
  username: '',
  email: '',
  password: '',
})

watch(() => props.initialTab, (value) => {
  activeTab.value = value
})

const redirectTarget = computed(() => String(props.redirect || route.query.redirect || '/'))

const activeAssist = computed(() => {
  if (activeTab.value === 'register') {
    return {
      lead: '创建账号后即可开始上传合同、查看审查结果并管理订单。',
      items: [
        '注册后即可开始首份合同审查。',
        '你的订单、报告和上传记录都会保存在当前账号下。',
      ],
    }
  }

  return {
    lead: '已有账号可直接查看合同、订单和报告记录，继续当前使用。',
    items: [
      '待支付订单、会员状态和已购报告会自动显示。',
      '登录后可继续上传合同、查看报告和完成支付。',
    ],
  }
})

const activeScene = computed(() => {
  if (activeTab.value === 'register') {
    return {
      badge: '新用户注册',
      topline: '开通账号即可开始使用',
      title: '注册后即可开始使用合同审查服务',
      lead: '完成注册后即可上传合同、查看风险摘要、完成支付并导出报告，后续记录也会持续保留在你的账号中。',
      metricLabel: '账号状态',
      metricValue: '注册并开始使用',
      sideMetricLabel: '推荐起点',
      sideMetricValue: '上传首份合同',
      lines: ['创建账号', '完善信息', '开始使用'],
    }
  }

  return {
    badge: '账号登录',
    topline: '登录后继续使用服务',
    title: '登录后继续查看合同与报告',
    lead: '使用已有账号查看上传记录、订单信息、报告内容和会员状态，继续使用当前服务。',
    metricLabel: '登录状态',
    metricValue: '登录并继续使用',
    sideMetricLabel: '登录后可用',
    sideMetricValue: '查看记录与报告',
    lines: ['登录账号', '查看记录', '继续使用'],
  }
})

async function submitLogin() {
  try {
    submitting.value = true
    await auth.login(loginForm.email, loginForm.password)
    ElMessage.success('登录成功')
    if (props.embedded) {
      emit('authenticated', redirectTarget.value)
      return
    }
    await router.replace(redirectTarget.value)
  } catch {
    ElMessage.error('登录失败，请检查邮箱或密码')
  } finally {
    submitting.value = false
  }
}

async function submitRegister() {
  try {
    submitting.value = true
    await auth.register(registerForm.email, registerForm.username, registerForm.password)
    ElMessage.success('注册成功')
    if (props.embedded) {
      emit('authenticated', redirectTarget.value)
      return
    }
    await router.replace(redirectTarget.value)
  } catch {
    ElMessage.error('注册失败，请检查输入信息')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.auth-embed {
  width: 100%;
}

.auth-embed__card {
  width: 100%;
}

.auth-shell {
  display: grid;
  grid-template-columns: minmax(280px, 0.92fr) minmax(320px, 1fr);
  min-height: 560px;
  background:
    linear-gradient(135deg, rgba(238, 246, 255, 0.9) 0%, rgba(255, 255, 255, 0.94) 42%, rgba(247, 252, 255, 0.96) 100%);
}

.auth-showcase {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 42px 34px 36px;
  color: #0f172a;
  background:
    linear-gradient(165deg, rgba(7, 89, 133, 0.96) 0%, rgba(14, 116, 144, 0.92) 30%, rgba(8, 145, 178, 0.78) 100%);
  overflow: hidden;
}

.auth-showcase::before,
.auth-showcase::after {
  content: '';
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
}

.auth-showcase::before {
  width: 220px;
  height: 220px;
  top: -76px;
  right: -82px;
  background: rgba(255, 255, 255, 0.12);
}

.auth-showcase::after {
  width: 160px;
  height: 160px;
  bottom: -68px;
  left: -54px;
  background: rgba(255, 255, 255, 0.08);
}

.auth-showcase__badge,
.auth-showcase__topline,
.auth-showcase__title,
.auth-showcase__lead,
.auth-showcase__caption,
.auth-assist,
.auth-card__metrics,
.auth-scene-chipbar {
  position: relative;
  z-index: 1;
}

.auth-showcase__badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #ecfeff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.auth-showcase__topline {
  margin: 0;
  color: rgba(236, 254, 255, 0.78);
  font-size: 13px;
  font-weight: 700;
}

.auth-showcase__title {
  margin: 0;
  color: #f8fafc;
  font-size: 30px;
  line-height: 1.18;
  letter-spacing: -0.03em;
}

.auth-showcase__lead {
  margin: 0;
  color: rgba(236, 254, 255, 0.9);
  font-size: 15px;
  line-height: 1.8;
}

.auth-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 42px 38px 38px;
}

.auth-panel__head {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}

.auth-card__eyebrow {
  margin: 0;
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.auth-panel__title {
  margin: 8px 0 0;
  color: #0f172a;
  font-size: 28px;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.auth-switcher {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 6px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(226, 232, 240, 0.64) 0%, rgba(241, 245, 249, 0.82) 100%);
  border: 1px solid rgba(203, 213, 225, 0.72);
  margin-bottom: 24px;
}

.auth-switcher__item {
  min-height: 48px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: #475569;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.18s ease;
}

.auth-switcher__item--active {
  color: #0f172a;
  background: #ffffff;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}

.auth-form--panel {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.auth-form--panel :deep(.el-form-item) {
  margin-bottom: 16px;
}

.auth-form--panel :deep(.el-form-item__label) {
  padding-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.auth-form--panel :deep(.el-input__wrapper) {
  min-height: 50px;
  padding: 0 14px;
  border-radius: 16px;
  background: rgba(248, 250, 252, 0.92);
  box-shadow: inset 0 0 0 1px rgba(203, 213, 225, 0.92);
}

.auth-form--panel :deep(.el-input__wrapper.is-focus) {
  box-shadow:
    inset 0 0 0 1px rgba(37, 99, 255, 0.72),
    0 0 0 4px rgba(191, 219, 254, 0.48);
}

.auth-form--panel :deep(.el-input__inner) {
  color: #0f172a;
  font-size: 15px;
}

.auth-form__submit {
  min-height: 52px;
  margin-top: 6px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #0f4fe6 0%, #2563eb 46%, #06b6d4 100%);
  box-shadow: 0 18px 32px rgba(37, 99, 235, 0.28);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.auth-form__submit:hover {
  transform: translateY(-1px);
}

.auth-panel__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 18px 0 0;
  color: #64748b;
  font-size: 13px;
}

.auth-panel__link {
  border: 0;
  padding: 0;
  background: transparent;
  color: #0f4fe6;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.auth-assist {
  padding: 18px 18px 16px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.auth-assist__lead {
  margin: 0 0 12px;
  color: #f8fafc;
  font-size: 14px;
  line-height: 1.75;
}

.auth-assist__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.auth-assist__list li {
  position: relative;
  padding-left: 18px;
  color: rgba(236, 254, 255, 0.92);
  font-size: 13px;
  line-height: 1.7;
}

.auth-assist__list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #67e8f9;
  box-shadow: 0 0 0 4px rgba(103, 232, 249, 0.16);
}

.auth-scene-chipbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.auth-scene-chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(191, 219, 254, 0.14);
  color: rgba(226, 232, 240, 0.9);
  font-size: 12px;
  font-weight: 700;
}

.auth-scene-chip--primary {
  background: linear-gradient(135deg, rgba(37, 99, 255, 0.18) 0%, rgba(34, 211, 238, 0.18) 100%);
  border-color: rgba(125, 211, 252, 0.24);
  color: #f8fafc;
}

.auth-card__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.auth-card__metric {
  padding: 16px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(242, 247, 255, 0.9) 100%);
  border: 1px solid rgba(191, 219, 254, 0.28);
}

.auth-card__metric--accent {
  background: linear-gradient(135deg, rgba(37, 99, 255, 0.2) 0%, rgba(34, 211, 238, 0.18) 100%);
  border-color: rgba(255, 255, 255, 0.16);
}

.auth-card__metric span {
  display: block;
  font-size: 12px;
  color: #475569;
}

.auth-card__metric strong {
  display: block;
  margin-top: 8px;
  color: #0f172a;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .auth-shell {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .auth-showcase {
    padding: 26px 22px 22px;
    gap: 14px;
  }

  .auth-showcase__title {
    font-size: 24px;
  }

  .auth-panel {
    padding: 24px 20px 24px;
  }

  .auth-panel__title {
    font-size: 22px;
  }

  .auth-card__metrics {
    grid-template-columns: 1fr;
  }
}
</style>